import {
  OptimizationRunSourceType,
  OptimizeJobEngine,
} from '@warebee/frontend/data-access-api-graphql';
import classNames from 'classnames';
import _ from 'lodash';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useRecoilState, useRecoilValue } from 'recoil';
import DropdownSelector from '../../../components/actions/DropdownSelector';
import { InputGroupList } from '../../../components/inputs/InputGroupList';
import InputNumber from '../../../components/inputs/InputNumber';
import TitleSection from '../../../components/layout/TitleSection';
import { allocationRunSummary } from '../../../simulation/store/allocation/allocation.state';
import { optimisationUseAllocationRun } from '../../../simulation/store/optimisation.state';
import {
  optimisationSetupEngineType,
  optimisationSetupNodesCount,
  optimisationSetupSeed,
  optimizationSetupStepMultiplier,
} from '../../../simulation/store/optimisationSetup.state';
import { simulationLayoutSummary } from '../../../simulation/store/simulation.layout.state';
import { simulationCurrent } from '../../../simulation/store/simulation.state';
import useRunOptimization, {
  RunOptimizationParams,
} from '../../../warehouse/hooks/useRunOptimization';

export type OptimizationRunActionAdvancedProps = {
  className?: string;
  showAdvanced?: boolean;
};

const SettingsEngineOptimise: React.FC<
  OptimizationRunActionAdvancedProps
> = props => {
  const { t } = useTranslation('simulation');
  const layoutSummary = useRecoilValue(simulationLayoutSummary);

  const runOptimization = useRunOptimization();
  const [sim, updateSim] = useRecoilState(simulationCurrent);
  const totalLocationCount = layoutSummary?.locationCount ?? 0;
  const simId = sim?.id;
  // const [supportEnabled, setSupportEnabled] = useRecoilState(
  //   optimizationSetupSupportEnabled,
  // );
  const stepsMultiplier = useRecoilValue(optimizationSetupStepMultiplier);
  const [seed, setSeed] = useRecoilState(optimisationSetupSeed);
  const [nodesCount, setNodesCount] = useRecoilState(
    optimisationSetupNodesCount,
  );
  const [engineType, setEngineType] = useRecoilState(
    optimisationSetupEngineType,
  );
  const useAllocationRun = useRecoilValue(optimisationUseAllocationRun);
  const allocationRun = useRecoilValue(allocationRunSummary);

  const [stepsCount, setStepsCount] = useState<number>(0);

  useEffect(() => {
    setStepsCount(Math.floor(stepsMultiplier * totalLocationCount));
  }, [stepsMultiplier]);

  const optimisationEngineOptions: Record<
    OptimizeJobEngine,
    { title: string }
  > = {
    [OptimizeJobEngine.FARGATE]: {
      title: `F - Engine`,
    },
    [OptimizeJobEngine.MPI]: {
      title: `M - Engine`,
    },
    [OptimizeJobEngine.LAMBDA]: {
      title: `L - Engine`,
    },
  };

  const runOnAllocate = useAllocationRun && !_.isNil(allocationRun);
  const optRun: RunOptimizationParams = {
    id: runOnAllocate ? allocationRun?.id : (simId ?? ''),
    type: runOnAllocate
      ? OptimizationRunSourceType.ALLOCATION_RUN
      : OptimizationRunSourceType.SIMULATION,
    seed,
    steps: stepsCount,
    engine: engineType,
    nodes: nodesCount,
  };
  return (
    <>
      <TitleSection
        id="settings-engine-optimise"
        inPanelView
        title={t`Optimisation Settings (advanced)`}
        classNameInner="p-4"
        collapsible
      >
        <InputGroupList hasSpacing>
          <InputNumber
            className="mb-4"
            title={`Locations`}
            value={totalLocationCount}
            disabled
          />
          <InputNumber
            className="mb-4"
            title={`Optimisation Steps`}
            value={stepsCount}
            onChange={v => setStepsCount(v)}
          />
          <DropdownSelector
            classNameLabel={classNames('text-xxs xl:text-sm flex-1')}
            className={classNames(
              'ltr:mr-0.5 xl:ltr:mr-2 rtl:ml-0.5 xl:rtl:ml-2',
            )}
            DropAlignRight
            widthFull
            headerMode
            label={`Optimisation Engine`}
            onClick={e => {
              e.stopPropagation();
            }}
            value={engineType}
            values={
              Object.keys(optimisationEngineOptions) as OptimizeJobEngine[]
            }
            renderValue={value => optimisationEngineOptions[value].title}
            onChange={async selectedEngine => {
              setEngineType(selectedEngine as OptimizeJobEngine);
            }}
          />

          {engineType === OptimizeJobEngine.MPI && (
            <InputNumber
              className="mb-4"
              title={`Opt. Nodes`}
              value={nodesCount}
              onChange={setNodesCount}
            />
          )}

          {/* <InputNumber
              className="mt-4"
              title={`Opt. Seed`}
              value={optimizationSeed}
              onChange={setOptimizationSeed}
              /> */}
        </InputGroupList>
      </TitleSection>
      {/* </div>
      </PanelContainer> */}
    </>
  );
};

export default SettingsEngineOptimise;
